/**
 * OS's live mode, whole: the assistant's live call, its two levels, and why
 * the last one ended, for the OS composer that turns live in its own box
 * (docs/plans/os-voice-conversation.md, step 3.8).
 *
 * The owner (the conversation's composer) holds this hook and draws live
 * mode from it; nothing here draws.
 *
 *   const voice = useCoachVoice({ tenantId, context, onTurn: append });
 *   voice.begin()          Talk live
 *   voice.toggleMute()     the person's own Mute, the only thing that stops what is sent
 *   voice.live.interrupt() cut OS's speech now (Interrupt); the mic as it was
 *   voice.live.attend()    a click on the orb: what comes next is for OS
 *   voice.stop()           Stop live: the voice goes now, a question already running still lands
 *
 * `micLevel` moves the dot grid for every frame while not muted; `voiceLevel`
 * moves the orb while OS speaks (`level` is the same one, by its older
 * name). Keyboard never leaves live mode, so there is no Type instead here:
 * a line typed while live goes through the call (`live.say`).
 */
import type { AgentLiveEndedData, AssistantLiveContext, LiveEndReason, LiveSeat } from '@alluzo/shared';
import type { UIMessage } from 'ai';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useVoiceLevel, type VoiceLevel } from './level';
import { liveNoteOf, type LiveNoteKind } from './live-edges';
import { liveEndReasonOf, type LiveEnding } from './live-mode';
import { useAssistantLive, type AssistantLive, type LiveSeatNote, type StudioLiveFrame } from './use-assistant-live';

export type CoachVoiceInput = {
  readonly tenantId: string | null;
  /** The setup seat the call opens on (step 5.4); absent is the assistant. */
  readonly seat?: Exclude<LiveSeat, 'assistant'>;
  /** A setup seat's note on one of the person's lines. */
  readonly onNote?: (note: LiveSeatNote) => void;
  /** The studio seat's frames (7.5). */
  readonly onStudioFrame?: (frame: StudioLiveFrame) => void;
  /** What the call knows, read at the moment it starts: the thread open, and where the person is. */
  readonly context: () => AssistantLiveContext;
  /** One finished exchange, already kept on the server: append it to the thread. */
  readonly onTurn: (messages: UIMessage[], card: boolean, asked: boolean) => void;
  /** Whether the conversation, and so its cards, is in front of the person now. Absent, it always is. */
  readonly conversationShown?: () => boolean;
  /** A spoken yes came while the conversation was out of sight: bring it back. */
  readonly onCardHidden?: () => void;
  /** OS is minimised: the person is not looking at it. */
  readonly minimised?: boolean;
};

/** The divider live mode leaves in the thread as it ends, under the id the server keeps it by (`${callId}-end`). */
export type LiveDividerMark = { readonly id: string; readonly data: AgentLiveEndedData };

export type CoachVoice = {
  readonly live: AssistantLive;
  /** OS's voice, 0..1: the orb while it speaks. */
  readonly voiceLevel: VoiceLevel;
  /** The microphone, 0..1, for every frame while not muted: the dot grid. */
  readonly micLevel: VoiceLevel;
  /** `voiceLevel`, by its older name. */
  readonly level: VoiceLevel;
  /** Why the last call ended, until the next one starts; null while one is up, or before the first. */
  readonly ending: LiveEnding | null;
  /** The last call opened before it ended: false for one that never came up (no connection, the microphone refused). */
  readonly endedOpen: boolean;
  /** The server's own sentence for an ending, or the browser's for a mic that would not start. */
  readonly notice: string | null;
  /**
   * The divider the last call that opened left, until the next one starts:
   * the thread draws it where live mode ended. A call that never opened (its
   * mic refused) leaves none.
   */
  readonly divider: LiveDividerMark | null;
  /** What the thread says when live mode could not run, or another device has it (`liveNoteOf`); null while a call is up. */
  readonly note: LiveNoteKind | null;
  /** Talk live: a new call, carrying the thread. */
  readonly begin: () => void;
  /** The shipped live button: interrupt OS while it speaks, else attend (a legacy call: cut in and open the mic). */
  readonly talk: () => void;
  /** The person's own Mute. */
  readonly toggleMute: () => void;
  /** Stop live: the voice ends at once, and a question already running still lands in the thread. Answers how long live mode ran. */
  readonly stop: () => number;
  /** Forget why the last call ended: the line over the box goes. */
  readonly clearEnding: () => void;
};

export const useCoachVoice = (input: CoachVoiceInput): CoachVoice => {
  const called = useRef(input);
  useEffect(() => {
    called.current = input;
  });

  const voiceLevel = useVoiceLevel();
  const micLevel = useVoiceLevel();
  const [ending, setEnding] = useState<LiveEnding | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [divider, setDivider] = useState<LiveDividerMark | null>(null);
  /** The last ending came after the call opened. */
  const [endedOpen, setEndedOpen] = useState(false);
  // The call as the last render read it: an ending reads its open time and id.
  const opened = useRef<{ readonly since: number | null; readonly callId: string | null }>({ since: null, callId: null });
  /** Live mode ended for this reason: its divider, once per call (the close after an error does not write another). */
  const leave = useCallback((reason: LiveEndReason): void => {
    const { since, callId } = opened.current;
    if (since === null) return;
    const id = callId === null ? `live-${since}-end` : `${callId}-end`;
    const seconds = Math.max(0, Math.round((Date.now() - since) / 1000));
    setDivider((held) => (held?.id === id ? held : { id, data: { seconds, reason } }));
  }, []);

  const live = useAssistantLive({
    tenantId: input.tenantId,
    ...(input.seat === undefined ? {} : { seat: input.seat }),
    onNote: (note) => called.current.onNote?.(note),
    onStudioFrame: (frame) => called.current.onStudioFrame?.(frame),
    level: voiceLevel,
    micLevel,
    context: () => called.current.context(),
    conversationShown: () => called.current.conversationShown?.() ?? true,
    onCardHidden: () => called.current.onCardHidden?.(),
    minimised: input.minimised === true,
    onTurn: (messages, card, asked) => called.current.onTurn(messages, card, asked),
    // An error that ended the call has already said why; the close after it does not say it again.
    onClosed: (reason) => {
      setEndedOpen(opened.current.since !== null);
      leave(liveEndReasonOf(reason));
      setEnding((held) => held ?? (reason === 'idle' ? 'idle' : reason === 'quota' ? 'quota' : 'ended'));
    },
    onError: (code, message) => {
      setEndedOpen(opened.current.since !== null);
      if (code === 'MIC_OFF') setEnding('mic_off');
      else if (code === 'NO_MIC') setEnding('no_mic');
      else if (code === 'FAILED') {
        setEnding('mic_failed');
        if (message !== '') setNotice(message);
      }
      else if (code === 'AGENT_QUOTA') {
        leave('quota');
        setEnding('quota');
        setNotice(message === '' ? null : message);
      } else {
        // The server could not go on: OS could not answer, with Ask again.
        leave('provider');
        setEnding('failed');
      }
    },
  });

  useEffect(() => {
    opened.current = { since: live.since, callId: live.callId };
  });

  const { start, stopLive } = live;

  const clearEnding = useCallback(() => {
    setEnding(null);
    setNotice(null);
    setEndedOpen(false);
  }, []);

  const begin = useCallback(() => {
    clearEnding();
    start();
  }, [clearEnding, start]);

  const stop = useCallback((): number => {
    // Read before the call ends: Stop live after the call ended by itself leaves no second divider.
    const up = live.active;
    const ran = stopLive();
    if (up) leave('stopped');
    setEnding('stopped');
    setNotice(null);
    return ran;
  }, [stopLive, leave, live.active]);

  const note = live.active ? null : liveNoteOf({ ending, opened: endedOpen, elsewhere: live.elsewhere !== null });

  return { live, voiceLevel, micLevel, level: voiceLevel, ending, endedOpen, notice, divider, note, begin, talk: live.talk, toggleMute: live.toggleMute, stop, clearEnding };
};
